import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileText, Upload, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { INVOICE_STATUS, INVOICE_TYPES } from "./shared";

const EMPTY = { typ: "sonstige", titel: "", status: "offen", betrag: "", rechnungsnummer: "", rechnungsdatum: "", zahlungsdatum: "", notiz: "" };

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}

export function InvoiceDialog({ open, onOpenChange, project, invoice, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isNew = !invoice?.id;

  useEffect(() => {
    if (!open) return;
    const base = { ...EMPTY, ...(invoice || {}) };
    setForm({ ...base, betrag: base.betrag ?? "", titel: base.titel || "" });
  }, [open, invoice]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const payload = () => {
    const p = { ...form };
    const n = parseFloat(String(p.betrag).replace(/\./g, "").replace(",", "."));
    p.betrag = String(p.betrag).trim() === "" || Number.isNaN(n) ? null : n;
    ["rechnungsnummer", "rechnungsdatum", "zahlungsdatum", "notiz", "titel"].forEach((k) => { if (p[k] === "") p[k] = null; });
    if (p.status === "bezahlt" && !p.zahlungsdatum) p.zahlungsdatum = new Date().toISOString().slice(0, 10);
    delete p.id; delete p.file; delete p.created_at; delete p.updated_at; delete p.updated_by_name;
    if (!isNew) delete p.typ;
    return p;
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = isNew
        ? await api.post(`/rechnungen/projects/${project.id}`, payload())
        : await api.patch(`/rechnungen/projects/${project.id}/${invoice.id}`, payload());
      toast.success("Rechnung gespeichert");
      onSaved(); onOpenChange(false);
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Rechnung wirklich löschen?")) return;
    try {
      await api.delete(`/rechnungen/projects/${project.id}/${invoice.id}`);
      toast.success("Rechnung gelöscht"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/rechnungen/projects/${project.id}/${invoice.id}/file`, fd);
      setForm((f) => ({ ...f, file: data }));
      toast.success("PDF hochgeladen"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const openPdf = async () => {
    try {
      const res = await api.get(`/rechnungen/projects/${project.id}/${invoice.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("PDF konnte nicht geladen werden"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Neue Rechnung" : form.titel || INVOICE_TYPES[form.typ]}</DialogTitle>
          <DialogDescription>{project?.kunde_name}{project?.kunde_ort ? ` · ${project.kunde_ort}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {isNew && (
            <Field label="Rechnungsart">
              <Select value={form.typ} onValueChange={set("typ")}>
                <SelectTrigger data-testid="invoice-typ-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(INVOICE_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Bezeichnung">
            <Input value={form.titel} onChange={set("titel")} placeholder="z. B. Nachtrag Wallbox" data-testid="invoice-titel-input" />
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(INVOICE_STATUS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Betrag (€)">
            <Input inputMode="decimal" value={form.betrag} onChange={set("betrag")} placeholder="0,00" data-testid="invoice-betrag-input" />
          </Field>
          <Field label="Rechnungsnummer">
            <Input value={form.rechnungsnummer || ""} onChange={set("rechnungsnummer")} data-testid="invoice-nummer-input" />
          </Field>
          <Field label="Rechnungsdatum">
            <Input type="date" value={form.rechnungsdatum || ""} onChange={set("rechnungsdatum")} data-testid="invoice-datum-input" />
          </Field>
          <Field label="Zahlungseingang">
            <Input type="date" value={form.zahlungsdatum || ""} onChange={set("zahlungsdatum")} data-testid="invoice-zahlung-input" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Notiz">
              <Textarea rows={2} value={form.notiz || ""} onChange={set("notiz")} data-testid="invoice-notiz-input" />
            </Field>
          </div>
          <div className="sm:col-span-2 rounded-md border border-dashed border-border p-3 flex items-center gap-3 flex-wrap">
            <FileText className="h-4 w-4 text-primary" />
            {isNew ? (
              <span className="text-xs text-muted-foreground">PDF kann nach dem ersten Speichern hochgeladen werden.</span>
            ) : form.file ? (
              <button type="button" className="text-sm text-primary underline truncate max-w-[220px]" onClick={openPdf} data-testid="invoice-pdf-open">{form.file.name}</button>
            ) : (
              <span className="text-xs text-muted-foreground">Kein PDF abgelegt</span>
            )}
            {!isNew && (
              <>
                <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="invoice-pdf-input" />
                <Button type="button" size="sm" variant="outline" className="ml-auto" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="invoice-pdf-upload-btn">
                  {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                  {form.file ? "Ersetzen" : "PDF hochladen"}
                </Button>
              </>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          {!isNew && (
            <Button variant="ghost" className="text-destructive mr-auto" onClick={remove} data-testid="invoice-delete-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Löschen
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="invoice-cancel-btn">Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="invoice-save-btn">{saving ? "Speichert…" : "Speichern"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
